/**
 * Was ein evcc-Ladepunkt seit HM-165 außerdem kann — Fahrzeug, Plan, „immer
 * mit Mindestleistung", die Regelung des Hauses —, als Abschnitte für „Laden"
 * und den Reiter „Steuerung".
 *
 * Es gelten dieselben drei Regeln wie in `charge-point.tsx`: Eine Fläche
 * beauftragt und springt nicht um; ohne Verbindung wird nicht bedient; was die
 * Wallbox nicht schreibbar meldet **oder dieser Aufrufer nicht setzen darf**,
 * bekommt keine Fläche (Regel 10). Ob er darf, sagt die API
 * (`controllableSettings`) — ein Bewohner sieht die Regelung des Hauses nicht.
 *
 * **Ein Plan entsteht aus seiner Uhrzeit.** evcc setzt Ziel und Uhrzeit nur
 * zusammen, ein Auftrag trägt eine Zahl; das Gateway nimmt für einen neuen Plan
 * das Ladeziel, auf das evcc ohnehin lädt. Die Oberfläche schickt deshalb nur
 * „fertig um 7:30" — als Uhrzeit, die die API in der Zeitzone der Anlage
 * auflöst, nicht als Zeitpunkt, den der Browser in seiner eigenen errechnet.
 */
import { useState, type ReactNode } from "react";
import { Button, Card, Select, SegmentedControl, Switch } from "@hausmeter/ui";
import {
  ALWAYS_CHARGE_TEXTS,
  formatDuration,
  formatPlanTime,
  limitInForce,
  maySet,
  percentOf,
  planSummary,
  type AlwaysCharge,
  type ChargePoint,
  type ChargePointChange,
} from "../lib/charge-point.js";
import { SectionHeading } from "./section.js";
import { Stepper } from "./charge-point.js";

const MUTED = { fontSize: 12.5, lineHeight: 1.55, color: "var(--text-muted)" } as const;
const LABEL = { fontSize: 13, fontWeight: 600, color: "var(--text-body)" } as const;

interface SettingProps {
  readonly point: ChargePoint;
  readonly enabled: boolean;
  readonly send: (change: ChargePointChange) => void;
}

/** Ob eine Einstellung eine Fläche bekommt: gemeldet, schreibbar und für diesen Aufrufer erlaubt. */
function offered(
  point: ChargePoint,
  setting: ChargePointChange["setting"],
  field: { datapointId: string | null; writable: boolean },
): boolean {
  return maySet(point, setting) && field.datapointId !== null && field.writable;
}

function Column({ children }: { children: ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{children}</div>;
}

const GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 230px), 1fr))",
  gap: "var(--sp-5)",
  alignItems: "start",
} as const;

/* -------------------------------------------------------------------------- */

/**
 * **Welches Fahrzeug steht da?** Die Liste kommt vom Gateway (`vehicle-{n}`),
 * die Wahl geht als Nummer hinaus. Reichweite und Kilometerstand meldet evcc
 * nur angesteckt; an einem leeren Ladepunkt stehen sie nicht.
 */
export function VehicleSection({ point, enabled, send }: SettingProps) {
  const { vehicle } = point.state;
  if (vehicle.selected.datapointId === null) return null;
  const choosable = offered(point, "vehicle", vehicle.selected) && vehicle.choices.length > 0;

  const facts = [
    vehicle.rangeKm.value === null ? null : `Reichweite ${Math.round(vehicle.rangeKm.value)} km`,
    vehicle.apiLimitSoc.value === null
      ? null
      : `Ladegrenze im Fahrzeug ${percentOf(vehicle.apiLimitSoc.value)} %`,
    vehicle.odometerKm.value === null
      ? null
      : `Kilometerstand ${Math.round(vehicle.odometerKm.value).toLocaleString("de-DE")} km`,
  ].filter((fact): fact is string => fact !== null);

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <SectionHeading title="Fahrzeug" />
      <Card>
        <div style={GRID}>
          {choosable ? (
            <Select
              label="Am Ladepunkt"
              disabled={!enabled}
              value={vehicle.selected.value === null ? "" : String(vehicle.selected.value)}
              options={[
                { value: "", label: "Kein Fahrzeug" },
                ...vehicle.choices.map((choice) => ({
                  value: String(choice.number),
                  label: choice.title,
                })),
              ]}
              onChange={(event) => {
                const next = event.target.value === "" ? null : Number(event.target.value);
                if (next !== vehicle.selected.value) send({ setting: "vehicle", value: next });
              }}
            />
          ) : (
            <Column>
              <span style={LABEL}>Am Ladepunkt</span>
              <span style={{ fontSize: 14, color: "var(--text-strong)" }}>
                {vehicle.title ?? "Kein Fahrzeug gewählt"}
              </span>
            </Column>
          )}
          {offered(point, "vehicleLimitSoc", vehicle.limitSoc) &&
            vehicle.selected.value !== null && (
              <Stepper
                label="Ladeziel des Fahrzeugs"
                unit="%"
                field={vehicle.limitSoc}
                toShown={percentOf}
                fromShown={(percent) => percent / 100}
                min={20}
                max={100}
                step={5}
                enabled={enabled}
                hint="Gilt für jede Ladung dieses Fahrzeugs; das Ladeziel einer Sitzung geht vor."
                onSend={(value) => send({ setting: "vehicleLimitSoc", value })}
              />
            )}
          {offered(point, "vehicleMinSoc", vehicle.minSoc) && vehicle.selected.value !== null && (
            <Stepper
              label="Mindestladestand"
              unit="%"
              field={vehicle.minSoc}
              toShown={percentOf}
              fromShown={(percent) => percent / 100}
              min={0}
              max={100}
              step={5}
              enabled={enabled}
              hint="Bis zu diesem Stand lädt evcc sofort — auch aus dem Netz, auch im Sonnenbetrieb."
              onSend={(value) => send({ setting: "vehicleMinSoc", value })}
            />
          )}
        </div>
        {facts.length > 0 && (
          <p style={{ ...MUTED, margin: "var(--sp-4) 0 0" }}>{facts.join(" · ")}</p>
        )}
      </Card>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * **Bis wann soll es fertig sein?** Ein Plan am Fahrzeug (Ladestand), sonst am
 * Ladepunkt (Energie). Gezeigt wird der Plan, der gilt — auch einer, der in
 * evcc wiederkehrend eingerichtet ist; ändern lässt sich hier nur der
 * einmalige.
 */
export function PlanSection({ point, enabled, send }: SettingProps) {
  const { plan, vehicle } = point.state;
  const [clock, setClock] = useState("07:00");
  const summary = planSummary(point);

  const socPlan =
    vehicle.selected.value !== null && offered(point, "planSocTime", plan.socTime)
      ? ("planSocTime" as const)
      : null;
  const energyPlan = offered(point, "planEnergyTime", plan.energyTime)
    ? ("planEnergyTime" as const)
    : null;
  const timeSetting = socPlan ?? energyPlan;
  if (timeSetting === null && summary === null) return null;

  /* Das Ziel eines neuen Plans nimmt das Gateway selbst — ohne eines lehnt es ab. */
  const fallbackTarget =
    timeSetting === "planSocTime"
      ? limitInForce(point) === null
        ? null
        : `${percentOf(limitInForce(point)!)} %`
      : point.state.limitEnergyWh.value === null
        ? null
        : `${String(Math.round(point.state.limitEnergyWh.value / 100) / 10).replace(".", ",")} kWh`;
  const ownPlan = summary !== null && summary.kind !== "effective";
  const target = ownPlan ? summary.target : fallbackTarget;

  const progress = [
    plan.active.value === true ? "lädt gerade nach Plan" : null,
    plan.active.value !== true && plan.projectedStart.value !== null
      ? `beginnt voraussichtlich ${formatPlanTime(plan.projectedStart.value, point.timezone)}`
      : null,
    plan.overrunS.value !== null && plan.overrunS.value > 0
      ? `wird ${formatDuration(plan.overrunS.value)} später fertig`
      : null,
  ].filter((fact): fact is string => fact !== null);

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <SectionHeading title="Fertig bis" />
      <Card>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          <span style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text-body)" }}>
            {summary === null ? (
              "Kein Plan. Geladen wird nach der Betriebsart."
            ) : (
              <>
                <strong style={{ color: "var(--text-strong)" }}>
                  {summary.target} bis {formatPlanTime(summary.time, point.timezone)}
                </strong>
                {summary.kind === "effective" && " — ein Plan aus evcc"}
                {progress.length > 0 && `, ${progress.join(", ")}`}.
              </>
            )}
          </span>

          {timeSetting !== null && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 9, alignItems: "flex-end" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={LABEL}>Fertig um</span>
                <input
                  type="time"
                  value={clock}
                  disabled={!enabled}
                  onChange={(event) => setClock(event.target.value)}
                  style={{
                    height: 34,
                    padding: "0 10px",
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--field-font-md)",
                    border: "1px solid var(--border-default)",
                    borderRadius: "var(--radius-md)",
                    background: "var(--surface-card)",
                    color: "var(--text-strong)",
                  }}
                />
              </label>
              <Button
                variant="primary"
                size="sm"
                disabled={!enabled || target === null || !/^\d{2}:\d{2}$/.test(clock)}
                onClick={() => send({ setting: timeSetting, value: clock })}
              >
                {ownPlan ? "Verschieben" : "Plan setzen"}
              </Button>
              {ownPlan && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!enabled}
                  onClick={() =>
                    send({
                      setting: summary.kind === "soc" ? "planSocTime" : "planEnergyTime",
                      value: null,
                    })
                  }
                >
                  Plan entfernen
                </Button>
              )}
            </div>
          )}
          {timeSetting !== null && (
            <span style={MUTED}>
              {target === null
                ? "Für einen Plan braucht es ein Ziel — erst ein Ladeziel oder ein Energieziel setzen."
                : `Die nächste ${clock} Uhr am Ort der Anlage. Ziel: ${target}${ownPlan ? "" : ", das Ladeziel, das gerade gilt"}.`}
            </span>
          )}
        </div>
      </Card>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * **Immer mit Mindestleistung** (evcc ≥ 0.316) — und das Energieziel der
 * Sitzung. Beide gelten für diese Ladung, nicht für die Anlage.
 */
export function SessionSettingsSection({ point, enabled, send }: SettingProps) {
  const { alwaysCharge, limitEnergyWh } = point.state;
  const withAlways = offered(point, "alwaysCharge", alwaysCharge);
  const withEnergy = offered(point, "limitEnergy", limitEnergyWh);
  if (!withAlways && !withEnergy) return null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <SectionHeading title="Diese Ladung" />
      <Card>
        <div style={GRID}>
          {withAlways && (
            <Column>
              <span style={LABEL}>Immer mit Mindestleistung</span>
              {alwaysCharge.value === null ? (
                <span style={MUTED}>noch kein Wert gemeldet</span>
              ) : (
                <div
                  style={{
                    opacity: enabled ? 1 : 0.6,
                    pointerEvents: enabled ? "auto" : "none",
                  }}
                >
                  <SegmentedControl<AlwaysCharge>
                    size="sm"
                    label="Immer mit Mindestleistung"
                    value={alwaysCharge.value}
                    options={(["off", "on", "once"] as const).map((value) => ({
                      value,
                      label: ALWAYS_CHARGE_TEXTS[value].label,
                    }))}
                    onChange={(value) => {
                      if (value !== alwaysCharge.value) send({ setting: "alwaysCharge", value });
                    }}
                  />
                </div>
              )}
              <span style={{ fontSize: 12, lineHeight: 1.45, color: "var(--text-muted)" }}>
                {alwaysCharge.value === null
                  ? ALWAYS_CHARGE_TEXTS.on.text
                  : ALWAYS_CHARGE_TEXTS[alwaysCharge.value].text}{" "}
                „Bis zum Abstecken“ erscheint oben als „Min + PV“.
              </span>
            </Column>
          )}
          {withEnergy && (
            <Column>
              <Stepper
                label="Energieziel"
                unit="kWh"
                field={limitEnergyWh}
                toShown={(wh) => Math.round(wh / 1000)}
                fromShown={(kwh) => kwh * 1000}
                min={1}
                max={100}
                enabled={enabled}
                hint="Die Ladung endet nach dieser Energie — für ein Fahrzeug, das seinen Ladestand nicht meldet."
                onSend={(value) => send({ setting: "limitEnergy", value })}
              />
              {limitEnergyWh.value !== null && (
                <div>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!enabled}
                    onClick={() => send({ setting: "limitEnergy", value: null })}
                  >
                    Energieziel entfernen
                  </Button>
                </div>
              )}
            </Column>
          )}
        </div>
      </Card>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * **Die Regelung im Haus** — was nur setzt, wem die Anlage gehört: Vorrang,
 * Schaltschwellen, Verzögerungen, Hausakku-Boost, „günstig laden" und der
 * Einspeisevorrang. Ein Bewohner sieht den Abschnitt nicht (ADR 0036 Nachtrag).
 */
export function RegulationSection({ point, enabled, send }: SettingProps) {
  const s = point.state;
  const costUnit = s.smartCost.unit;
  const rows: ReactNode[] = [];

  if (offered(point, "priority", s.priority)) {
    rows.push(
      <Stepper
        key="priority"
        label="Vorrang"
        unit=""
        field={s.priority}
        min={0}
        max={10}
        enabled={enabled}
        hint="Bei knappem Überschuss bekommt der Ladepunkt mit dem höheren Vorrang zuerst."
        onSend={(value) => send({ setting: "priority", value })}
      />,
    );
  }
  if (offered(point, "enableThreshold", s.enableThresholdW)) {
    rows.push(
      <Stepper
        key="enable-threshold"
        label="Einschaltschwelle"
        unit="W"
        field={s.enableThresholdW}
        min={-100_000}
        max={100_000}
        step={100}
        enabled={enabled}
        hint="Ab diesem Netzwert beginnt Laden mit Überschuss; negativ heißt Einspeisung."
        onSend={(value) => send({ setting: "enableThreshold", value })}
      />,
    );
  }
  if (offered(point, "disableThreshold", s.disableThresholdW)) {
    rows.push(
      <Stepper
        key="disable-threshold"
        label="Abschaltschwelle"
        unit="W"
        field={s.disableThresholdW}
        min={-100_000}
        max={100_000}
        step={100}
        enabled={enabled}
        hint="Ab diesem Netzbezug endet Laden mit Überschuss."
        onSend={(value) => send({ setting: "disableThreshold", value })}
      />,
    );
  }
  for (const [setting, field, label] of [
    ["enableDelay", s.enableDelayS, "Einschaltverzögerung"],
    ["disableDelay", s.disableDelayS, "Abschaltverzögerung"],
  ] as const) {
    if (!offered(point, setting, field)) continue;
    rows.push(
      <Stepper
        key={setting}
        label={label}
        unit="min"
        field={field}
        toShown={(seconds) => Math.round(seconds / 60)}
        fromShown={(minutes) => minutes * 60}
        min={0}
        max={1440}
        enabled={enabled}
        hint="So lange muss die Schwelle anliegen, bevor evcc schaltet."
        onSend={(value) => send({ setting, value })}
      />,
    );
  }
  if (offered(point, "batteryBoost", s.batteryBoost)) {
    rows.push(
      <Column key="battery-boost">
        <span style={LABEL}>Hausakku-Boost</span>
        <Switch
          label="Für diese Ladung aus dem Hausakku laden"
          checked={s.batteryBoost.value === true}
          disabled={!enabled || s.batteryBoost.value === null}
          onChange={(checked) => send({ setting: "batteryBoost", value: checked })}
        />
      </Column>,
    );
  }
  if (offered(point, "batteryBoostLimit", s.batteryBoostLimit)) {
    rows.push(
      <Stepper
        key="battery-boost-limit"
        label="Hausakku-Boost bis"
        unit="%"
        field={s.batteryBoostLimit}
        toShown={percentOf}
        fromShown={(percent) => percent / 100}
        min={0}
        max={100}
        step={5}
        enabled={enabled}
        hint="Bis zu diesem Ladestand des Hausakkus wird für den Boost entladen."
        onSend={(value) => send({ setting: "batteryBoostLimit", value })}
      />,
    );
  }
  if (offered(point, "smartCostLimit", s.smartCost.limit) && costUnit !== null) {
    rows.push(
      <PriceLimit
        key="smart-cost"
        label="Günstig laden unter"
        limit={s.smartCost}
        unit={costUnit}
        enabled={enabled}
        hint="Unter diesem Preis oder CO₂-Wert lädt evcc sofort, auch aus dem Netz."
        onSend={(value) => send({ setting: "smartCostLimit", value })}
      />,
    );
  }
  if (
    offered(point, "smartFeedInPriorityLimit", s.smartFeedInPriority.limit) &&
    s.smartFeedInPriority.unit !== null
  ) {
    rows.push(
      <PriceLimit
        key="feed-in"
        label="Einspeisevorrang über"
        limit={s.smartFeedInPriority}
        unit={s.smartFeedInPriority.unit}
        enabled={enabled}
        hint="Über dieser Vergütung wird eingespeist statt geladen."
        onSend={(value) => send({ setting: "smartFeedInPriorityLimit", value })}
      />,
    );
  }
  if (rows.length === 0) return null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <SectionHeading
        title="Regelung im Haus"
        subtitle="Wer wann Überschuss und Hausakku bekommt. Setzt nur, wem die Anlage gehört."
      />
      <Card>
        <div style={GRID}>{rows}</div>
      </Card>
    </section>
  );
}

/**
 * Eine Preisgrenze: Cent je kWh oder Gramm CO₂ je kWh, mit „Entfernen". `0`
 * ist hier eine Grenze und kein Entfernen — das geht über den eigenen Knopf.
 */
function PriceLimit({
  label,
  limit,
  unit,
  enabled,
  hint,
  onSend,
}: {
  label: string;
  limit: ChargePoint["state"]["smartCost"];
  unit: "EUR/kWh" | "g/kWh";
  enabled: boolean;
  hint: string;
  onSend: (value: number | null) => void;
}) {
  const inCent = unit === "EUR/kWh";
  return (
    <Column>
      <Stepper
        label={label}
        unit={inCent ? "ct/kWh" : "g/kWh"}
        field={limit.limit}
        toShown={(value) => Math.round(inCent ? value * 100 : value)}
        fromShown={(shown) => (inCent ? shown / 100 : shown)}
        min={inCent ? -100 : 0}
        max={inCent ? 100 : 1000}
        step={inCent ? 1 : 10}
        enabled={enabled}
        hint={limit.active.value === true ? `${hint} Gerade aktiv.` : hint}
        onSend={onSend}
      />
      {limit.limit.value !== null && (
        <div>
          <Button variant="ghost" size="sm" disabled={!enabled} onClick={() => onSend(null)}>
            Grenze entfernen
          </Button>
        </div>
      )}
    </Column>
  );
}
